/**
 * Preferred driver: what the customer types on the Details step and what the
 * lookup said about it.
 *
 * The customer types the driver's phone number or number plate. orders-service
 * decides whether that driver can be reserved (active, vetted, on a fitting
 * vehicle, and has delivered for this company in the last 180 days) and only
 * ever answers with a first name or a single "can't reserve" message, so a
 * number that isn't a driver and a driver who isn't eligible look the same.
 *
 * The check here is only a shape check, so an obvious typo is caught before
 * a lookup is spent (orders allows 10 per user per 10 minutes). The server
 * normalises and decides.
 *
 * Pure: no Vue, no axios.
 */

export type PreferredInputKind = "phone" | "plate" | null;

/**
 * Upper case, separators removed. Spaces, dashes, dots and brackets: the same
 * separators orders-service's normalisePhone (helpers/phone.js) strips.
 */
export function compactPreferredInput(value: unknown): string {
  return (typeof value === "string" ? value : "").toUpperCase().replace(/[\s\-.()]/g, "");
}

/**
 * "phone" for a Kenyan mobile number in a form orders-service accepts (07…,
 * 01…, 2547…, +2547…; no bare 712…), "plate" for 4 to 10 letters and digits
 * with at least one of each, else null.
 */
export function preferredInputKind(value: unknown): PreferredInputKind {
  const s = compactPreferredInput(value);
  if (!s) return null;
  if (/^\+?\d+$/.test(s)) return /^(?:\+254|254|0)[17]\d{8}$/.test(s) ? "phone" : null;
  // Plates: orders strips only spaces and dashes, so a dot or bracket left in
  // one is not a plate there either.
  const plate = (typeof value === "string" ? value : "").toUpperCase().replace(/[\s-]/g, "");
  if (/^[A-Z0-9]{4,10}$/.test(plate) && /[A-Z]/.test(plate) && /\d/.test(plate)) return "plate";
  return null;
}

export const PREFERRED_INPUT_HINT = "Enter a phone number like 0712 345 678 or a plate like KDA 123A.";
export const PREFERRED_CANT_CHECK = "Can't check right now. You can still place the order.";
export const PREFERRED_UNAVAILABLE_FALLBACK = "We can't reserve this driver. Your order will go to nearby drivers.";

/**
 * Dedicated: the driver is only passed on to Xobo ops, who assign by hand.
 * Nothing is reserved or offered and the order never goes to nearby
 * drivers, so the On-Demand copy (25 seconds, nearby drivers) is replaced.
 */
export const PREFERRED_DEDICATED_HELP = "We'll pass this on to Xobo ops.";
export const PREFERRED_DEDICATED_UNAVAILABLE = "We can't find this driver for this vehicle. Xobo will assign one before pickup.";

export type PreferredLookupView =
  | { kind: "available"; text: string }
  | { kind: "unavailable"; text: string }
  | { kind: "invalid"; text: string }
  | { kind: "error"; text: string };

/** The lookup's 200 body as what the field shows. */
export function preferredLookupView(body: any, dedicated = false): PreferredLookupView {
  const d = body?.data;
  if (body?.status && d?.available === true) {
    const name = typeof d.first_name === "string" ? d.first_name.trim() : "";
    return {
      kind: "available",
      text: dedicated
        ? `✓ ${name || "Your driver"}. ${PREFERRED_DEDICATED_HELP}`
        : `✓ ${name || "Your driver"} gets this order first, for 25 seconds.`,
    };
  }
  if (body?.status && d?.available === false) {
    // orders' message is about nearby drivers, which Dedicated never uses.
    if (dedicated) return { kind: "unavailable", text: PREFERRED_DEDICATED_UNAVAILABLE };
    const msg = typeof d.message === "string" && d.message.trim() ? d.message : PREFERRED_UNAVAILABLE_FALLBACK;
    return { kind: "unavailable", text: msg };
  }
  return { kind: "error", text: PREFERRED_CANT_CHECK };
}

/**
 * A refused lookup (axios error) as what the field shows: a bad value says
 * how to type it; a rate limit, an outage or anything else says the order
 * can still be placed.
 */
export function preferredLookupErrorView(err: any): PreferredLookupView {
  const res = err?.response;
  if (res?.status === 400 && res?.data?.code === "preferred_driver_invalid") {
    return { kind: "invalid", text: PREFERRED_INPUT_HINT };
  }
  return { kind: "error", text: PREFERRED_CANT_CHECK };
}

/**
 * What createOrder gets: the typed value when it looks like a phone or a
 * plate, else nothing. A preference never blocks an order, so a value the
 * server would refuse is left out rather than sent.
 */
export function preferredDriverForOrder(value: unknown): { value: string } | null {
  const s = typeof value === "string" ? value.trim() : "";
  return s && preferredInputKind(s) ? { value: s } : null;
}

/**
 * The placed-order card's line when orders couldn't reserve the driver
 * (createOrder's data.preferred_driver), else null. A Dedicated order never
 * goes to nearby drivers, so its line says Xobo assigns one.
 */
export function preferredPlacedNotice(preferred: any, dedicated = false): string | null {
  if (preferred?.status !== "unavailable") return null;
  if (dedicated) return "We couldn't find the driver you asked for. Xobo will assign one before pickup.";
  return typeof preferred.message === "string" && preferred.message.trim()
    ? preferred.message
    : "We couldn't reserve this driver, so your order went to nearby drivers.";
}
